const REQUEST_TIMEOUT_MS = 30000
const INIT_TIMEOUT_MS = 5000

/**
 * Singleton iframe-based auth proxy for cross-origin authentication requests.
 * Uses postMessage to communicate with an iframe hosted on the identity service
 * that can make requests with HttpOnly cookies.
 *
 * Available operations:
 * - 'auth:status' - GET /lender/auth/status
 * - 'auth:login' - POST /lender/auth/login (form-urlencoded)
 * - 'auth:login-code' - POST /lender/auth/login/code (JSON)
 * - 'auth:login-token' - POST /lender/auth/login/token (JSON)
 * - 'auth:token' - POST /lender/auth/token (JSON)
 * - 'auth:logout' - POST /lender/auth/logout
 * - 'auth:accounts' - POST /lender/accounts (JSON)
 */
class IframeAuthProxy {
  constructor() {
    this.iframe = null
    this.ready = false
    this.pendingRequests = new Map()
    this.requestId = 0
    this.readyPromise = null
    this.initialized = false
    this.messageHandler = null
    this.availableOperations = []
  }

  init() {
    if (this.initialized) {
      return this.readyPromise
    }
    this.initialized = true

    this.readyPromise = new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Auth proxy initialization timeout'))
      }, INIT_TIMEOUT_MS)

      this.iframe = document.createElement('iframe')
      this.iframe.src = `${IDENTITY_SERVICE_URL}/lender/auth/frame`
      this.iframe.style.display = 'none'

      this.messageHandler = (event) => {
        if (event.origin !== window.location.origin &&
          event.origin !== IDENTITY_SERVICE_ORIGIN) {
          return
        }

        var data = event.data
        if (!data || typeof data !== 'object') {
          return
        }

        // Handle ready signal from iframe
        if (data.type === 'proxy-ready') {
          clearTimeout(timeoutId)
          this.ready = true
          this.availableOperations = data.operations || []
          resolve()
          return
        }

        // Handle pong response (for health checks)
        if (data.type === 'proxy-pong') {
          return
        }

        // Handle operations list response
        if (data.type === 'proxy-operations') {
          this.availableOperations = data.operations || []
          return
        }

        // Handle proxy response
        var pending = this.pendingRequests.get(data.id)
        if (pending) {
          if (data.success) {
            pending.resolve({
              status: data.status,
              statusText: data.statusText,
              data: data.data,
            })
          } else {
            pending.reject(new Error(data.error))
          }
          this.pendingRequests.delete(data.id)
        }
      }

      window.addEventListener('message', this.messageHandler)

      this.iframe.onerror = () => {
        clearTimeout(timeoutId)
        reject(new Error('Auth proxy iframe failed to load'))
      }

      this.iframe.onload = () => {
        // Iframe loaded, send a ping to request ready confirmation
        this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
      }

      document.body.appendChild(this.iframe)
    })

    return this.readyPromise
  }

  /**
   * Execute an operation by name.
   * @param {string} operation - The operation name (e.g., 'auth:status')
   * @param {Object} [options] - Request options
   * @param {Object} [options.body] - Request body
   * @param {Object} [options.headers] - Request headers (only allowed headers will be forwarded)
   * @returns {Promise<{status: number, statusText: string, data: any}>}
   */
  async operation(operation, options = {}) {
    // Initialize if not already done
    if (!this.initialized) {
      this.init()
    }

    // Wait for proxy to be ready
    await this.readyPromise

    var id = this.requestId++

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject })

      // Send request to the iframe using operation-based routing
      this.iframe.contentWindow.postMessage({
        type: 'proxy-request',
        id: id,
        operation: operation,
        body: options.body,
        headers: options.headers,
      }, IDENTITY_SERVICE_ORIGIN)

      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id)
          reject(new Error('Request timeout'))
        }
      }, REQUEST_TIMEOUT_MS)
    })
  }

  /**
   * Check if the proxy is healthy by sending a ping.
   * @returns {Promise<boolean>}
   */
  async ping() {
    if (!this.ready || !this.iframe) {
      return false
    }

    return new Promise((resolve) => {
      var timeout = setTimeout(() => resolve(false), 1000)

      var handler = (event) => {
        if (event.data && event.data.type === 'proxy-pong') {
          clearTimeout(timeout)
          window.removeEventListener('message', handler)
          resolve(true)
        }
      }

      window.addEventListener('message', handler)
      this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
    })
  }

  /**
   * Request list of available operations from the proxy.
   */
  listOperations() {
    if (!this.ready || !this.iframe) {
      return
    }
    this.iframe.contentWindow.postMessage({ type: 'proxy-list-operations' }, IDENTITY_SERVICE_ORIGIN)
  }

  /**
   * Get the list of available operations.
   * @returns {string[]}
   */
  getAvailableOperations() {
    return this.availableOperations
  }
}

// Singleton instance
export const iframeAuthProxy = new IframeAuthProxy()

export default iframeAuthProxy
